/**
 * Role ID to Role Code Mapping Utility
 * 
 * Maps backend role IDs (from JWT token) to frontend role strings.
 * This mapping is based on the identity_service.roles table in the database.
 * 
 * Database Role IDs (from AUTH_MIGRATION_FOR_RACK_DB.sql):
 * - 1: SUPER_ADMIN
 * - 2: ADMIN
 * - 3: AGENT
 * - 4: TEAM_LEAD
 * - 5: CUSTOMER
 * - 6: DEVELOPER
 */

/**
 * Role ID to Role Code Mapping
 * Maps numeric role IDs from backend to role codes
 */
export const ROLE_ID_TO_CODE = {
  1: 'SUPER_ADMIN',
  2: 'ADMIN',
  3: 'AGENT',
  4: 'TEAM_LEAD',
  5: 'CUSTOMER',
  6: 'DEVELOPER',
};

/**
 * Role Code to Frontend Role String Mapping
 * Maps backend role codes to frontend-compatible role strings
 */
export const ROLE_CODE_TO_FRONTEND_ROLE = {
  'SUPER_ADMIN': 'super-admin',
  'ADMIN': 'admin',
  'AGENT': 'agent',
  'TEAM_LEAD': 'team-lead',
  'CUSTOMER': 'customer',
  'DEVELOPER': 'developer',
};

/**
 * Map role ID to frontend role string
 * @param {number} roleId - Role ID from JWT token
 * @returns {string} Frontend role string (e.g., 'super-admin', 'agent')
 */
export const mapRoleIdToFrontendRole = (roleId) => {
  if (!roleId) return 'customer'; // Default to customer if no role ID
  
  const roleCode = ROLE_ID_TO_CODE[roleId];
  if (!roleCode) {
    console.warn(`Unknown role ID: ${roleId}, defaulting to 'customer'`);
    return 'customer';
  }
  
  const frontendRole = ROLE_CODE_TO_FRONTEND_ROLE[roleCode];
  if (!frontendRole) {
    console.warn(`Unknown role code: ${roleCode}, defaulting to 'customer'`);
    return 'customer';
  }
  
  return frontendRole;
};

/**
 * Map role code to frontend role string
 * @param {string} roleCode - Role code from backend (e.g., 'SUPER_ADMIN')
 * @returns {string} Frontend role string (e.g., 'super-admin')
 */
export const mapRoleCodeToFrontendRole = (roleCode) => {
  if (!roleCode) return 'customer';
  
  const frontendRole = ROLE_CODE_TO_FRONTEND_ROLE[roleCode.toUpperCase()];
  return frontendRole || 'customer';
};

/**
 * Get role code from role ID
 * @param {number} roleId - Role ID from JWT token
 * @returns {string} Role code (e.g., 'SUPER_ADMIN')
 */
export const getRoleCodeFromId = (roleId) => {
  return ROLE_ID_TO_CODE[roleId] || null;
};

/**
 * Get role ID from role code
 * @param {string} roleCode - Role code (e.g., 'SUPER_ADMIN')
 * @returns {number|null} Role ID or null if not found
 */
export const getRoleIdFromCode = (roleCode) => {
  const entries = Object.entries(ROLE_ID_TO_CODE);
  const entry = entries.find(([_, code]) => code === roleCode.toUpperCase());
  return entry ? parseInt(entry[0]) : null;
};

export default {
  ROLE_ID_TO_CODE,
  ROLE_CODE_TO_FRONTEND_ROLE,
  mapRoleIdToFrontendRole,
  mapRoleCodeToFrontendRole,
  getRoleCodeFromId,
  getRoleIdFromCode,
};

